import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Link,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

const headingColor = 'gray.900';
const textColor = 'gray.700';
const linkColor = '#135200';
const sectionBg = 'white';
const sectionBorder = 'gray.200';

// Extract plain text from React children (may be string, array, or element)
const extractText = (node) => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node?.props?.children) return extractText(node.props.children);
  return '';
};

// Match GitHub-flavored Markdown slug algorithm used in the .md TOC links
const slugify = (children) =>
  extractText(children)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')   // strip punctuation (?, ., etc.)
    .replace(/\s+/g, '-')       // spaces → hyphens
    .replace(/-+/g, '-')        // collapse multiple hyphens
    .trim();

/** Static Chakra UI overrides for react-markdown rendering. */
const markdownComponents = {
  h1: ({ children }) => (
    <Heading as="h1" id={slugify(children)} size="xl" color={headingColor} fontWeight="bold" mb={4} mt={6}>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" id={slugify(children)} size="lg" color={headingColor} fontWeight="bold" mb={3} mt={5}>
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" id={slugify(children)} size="md" color={headingColor} fontWeight="semibold" mb={2} mt={4}>
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text fontSize="md" color={textColor} lineHeight="relaxed" mb={4}>
      {children}
    </Text>
  ),
  a: ({ href, children }) => {
    const isExternal = href && (href.startsWith('http') || href.startsWith('//'));
    return (
      <Link
        href={href}
        color={linkColor}
        {...(isExternal ? { isExternal: true } : {})}
      >
        {children}
      </Link>
    );
  },
  ol: ({ children }) => (
    <Box as="ol" mb={4} sx={{ listStyleType: 'decimal', listStylePosition: 'outside', paddingInlineStart: '1.25em' }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box as="ul" mb={4} sx={{ listStyleType: 'disc', listStylePosition: 'outside', paddingInlineStart: '1.25em' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box as="li" color={textColor} mb={2} ml={0}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box pl={4} borderLeft="3px solid" borderColor="#52c41a" mb={4}>
      {children}
    </Box>
  ),
};

/**
 * Fetches a markdown file and renders it inside a styled page layout.
 * @param {string} fetchPath - Public path to the .md file (e.g. "/assets/privacy-policy.md")
 * @param {string} pageTitle - Display title shown as the page heading
 */
const MarkdownPage = ({ fetchPath, pageTitle }) => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMarkdownFile = async () => {
      try {
        const response = await fetch(fetchPath);
        if (!response.ok) {
          throw new Error(`Failed to load ${pageTitle}`);
        }
        const content = await response.text();
        setMarkdownContent(content);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadMarkdownFile();
  }, [fetchPath, pageTitle]);

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" pt={20} pb={20}>
        <Container maxW="container.lg">
          <VStack spacing={8} align="center" justify="center" minH="50vh">
            <Spinner size="xl" color="#389e0d" />
            <Text color={textColor}>Loading {pageTitle}...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" pt={20} pb={20}>
        <Container maxW="container.lg">
          <VStack spacing={8} align="start">
            <Heading size="2xl" color={headingColor} fontWeight="bold">
              {pageTitle}
            </Heading>
            <Alert status="error">
              <AlertIcon />
              Error loading {pageTitle}: {error}
            </Alert>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box as="main" minH="100vh" bg="gray.50" pt={20} pb={20}>
      <Container maxW="container.lg">
        <VStack spacing={8} align="start">
          <VStack spacing={4} align="start" w="full">
            <Heading size="2xl" color={headingColor} fontWeight="bold">
              {pageTitle}
            </Heading>
          </VStack>

          <Box
            w="full"
            bg={sectionBg}
            p={8}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={sectionBorder}
            shadow="sm"
          >
            <ReactMarkdown components={markdownComponents}>
              {markdownContent}
            </ReactMarkdown>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default MarkdownPage;
